import { useTranslation } from 'react-i18next';
import type { PriceCalcPricingSource } from '@hero/api';
import type { Currency } from '@hero/schema';
import { usePublishOrderStore, useOrderDraftStore } from '@hero/hooks-shared';
import { PencilIcon } from '@hero/web-icons';
import { Price } from '../../../../app/Price';
import { PriceCategoryBadge } from '../../../../app/PriceCategoryBadge';

export interface DestinationPriceChipProps {
  /** 0-based destination index in the source store. */
  index: number;
  /** Click target — opens the per-destination price-edit drawer. */
  onEdit: () => void;
  /**
   * Which store backs this chip. `'publish'` (default) reads the
   * singleton `usePublishOrderStore` used by the publish page;
   * `'order-draft'` reads the scoped `useOrderDraftStore` exposed by
   * an `<OrderDraftProvider>` (agent confirmation card, reorder
   * modal, edit drawer). Both stores share the same shape so the
   * chip's visual logic is identical regardless of source.
   */
  source?: 'publish' | 'order-draft';
}

/**
 * Per-destination price chip. Renders one of:
 *
 *   - **One-time** override the user typed (highest priority — the
 *     `Custom price` badge makes the override unmistakeable);
 *   - resolver-provided price for this leg with the matching origin
 *     badge (`Agreement price` / `Public rate` / `Awaiting price`);
 *   - "Awaiting price" placeholder when neither has landed yet (lane
 *     not fully picked, or the resolver returned `unresolved`).
 *
 * The badge is the shared `<PriceCategoryBadge>`, so an emerald
 * "Agreement price" here means exactly what it means in the price-edit
 * drawer, the location-change modal, and an agent card.
 *
 * The whole chip is clickable so the user can open the edit-price
 * drawer on this specific destination — same drawer the order-drawer
 * uses, in `publish-draft` mode (writes to `setPriceOverride`
 * instead of `useOrderUpdateApi`).
 */
export function DestinationPriceChip({
  index,
  onEdit,
  source = 'publish',
}: DestinationPriceChipProps) {
  if (source === 'order-draft') {
    return <DestinationPriceChipScoped index={index} onEdit={onEdit} />;
  }
  return <DestinationPriceChipPublish index={index} onEdit={onEdit} />;
}

interface DestinationPriceChipInnerProps {
  index: number;
  onEdit: () => void;
}

function DestinationPriceChipPublish({ index, onEdit }: DestinationPriceChipInnerProps) {
  const pricePreview = usePublishOrderStore(s => s.pricePreview);
  const override = usePublishOrderStore(s => s.priceOverrides[index]);
  return (
    <PriceChip
      onEdit={onEdit}
      override={override}
      route={pricePreview?.routes?.[index]}
      fallbackCurrency={pricePreview?.currency ?? 'SAR'}
    />
  );
}

/**
 * Scoped variant — reads the `useOrderDraftStore` exposed by the
 * caller's `<OrderDraftProvider>`. Same renderer as the publish
 * variant; the only difference is which store backs it.
 */
function DestinationPriceChipScoped({ index, onEdit }: DestinationPriceChipInnerProps) {
  const pricePreview = useOrderDraftStore(s => s.pricePreview);
  const override = useOrderDraftStore(s => s.priceOverrides[index]);
  return (
    <PriceChip
      onEdit={onEdit}
      override={override}
      route={pricePreview?.routes?.[index]}
      fallbackCurrency={pricePreview?.currency ?? 'SAR'}
    />
  );
}

interface PriceChipProps {
  onEdit: () => void;
  override?: { itemsOriginalSubtotal: number; currency: Currency };
  route?: { pricingSource: PriceCalcPricingSource; amount: { subtotal: number; currency: Currency } };
  fallbackCurrency: Currency;
}

function PriceChip({ onEdit, override, route, fallbackCurrency }: PriceChipProps) {
  const { t } = useTranslation('m-order');
  const ariaLabel = t('drawer.price_edit.title', { defaultValue: 'Edit price' });

  // An override always wins — the user explicitly locked a price for this
  // destination, whatever the resolver had to say about the lane.
  if (override) {
    return (
      <ChipButton onClick={onEdit} ariaLabel={ariaLabel}>
        <PriceCategoryBadge category="one_time" size="sm" />
        <Price
          amount={override.itemsOriginalSubtotal}
          currency={override.currency}
          size="sm"
          className="font-semibold"
        />
        <PencilIcon className="size-3.5 text-gray-400" />
      </ChipButton>
    );
  }

  // Lane not fully priced yet (or the resolver hasn't landed a response for
  // this destination). Render a soft "awaiting" affordance so the user still
  // sees they can set the price manually.
  if (!route) {
    return (
      <ChipButton onClick={onEdit} ariaLabel={ariaLabel}>
        <span className="flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-500">
          <span className="inline-block size-1.5 rounded-full bg-gray-300" />
          {t('wizard.price_source.empty', {
            defaultValue: 'Pricing appears once the lane is set',
          })}
        </span>
        <Price amount={0} currency={fallbackCurrency} size="sm" className="font-semibold" />
        <PencilIcon className="size-3.5 text-gray-400" />
      </ChipButton>
    );
  }

  return (
    <ChipButton onClick={onEdit} ariaLabel={ariaLabel}>
      <PriceCategoryBadge source={route.pricingSource} size="sm" />
      <Price
        amount={route.amount.subtotal}
        currency={route.amount.currency}
        size="sm"
        className="font-semibold"
      />
      <PencilIcon className="size-3.5 text-gray-400" />
    </ChipButton>
  );
}

interface ChipButtonProps {
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

function ChipButton({ onClick, ariaLabel, children }: ChipButtonProps) {
  return (
    <button
      type="button"
      onClick={e => {
        // Destination cards have a global click that re-opens the
        // location picker. Stop propagation so clicking the price
        // chip lands the edit-price drawer cleanly.
        e.stopPropagation();
        onClick();
      }}
      aria-label={ariaLabel}
      className="flex items-center gap-2 rounded-full border border-transparent px-1 py-0.5 transition-colors hover:border-gray-200 hover:bg-gray-50">
      {children}
    </button>
  );
}
